<template>
  <div>
    <div class="container max-w-3xl mb-16" v-loading="loading">
      <div class="mt-2">
        <div v-if="!isLiteSubscriber">
          <NewAIBuilder
            :blueprintGuid="blueprintGuid"
            :isNew="isNew"
            :generating="generating"
            :disabled="disableAIBuilder"
            :estimatedCompletionTime="aiEstimatedTime"
          />
          <transition name="slide-up-down" mode="out-in">
            <NewTemplatePicker @change="handleTemplateChange" :selectedTemplate="selectedTemplate" v-if="!generating" />
          </transition>
          <div class="container max-w-screen-xl mb-16"></div>
        </div>
      </div>

      <div>
        <flow-step-offer
          v-if="initialOfferStep && initialOfferStep.enabled"
          :step="initialOfferStep"
          ref="offer"
          @update="(update, field) => stepUpdate(initialOfferStep, update, field)"
          :key="initialOfferStep.guid"
          @rework="reworkStep(initialOfferStep)"
        />
        <flow-step-offer-empty v-else :step="initialOfferStep" @cta-click="enableStep(STEP_PLACE.INITIAL_OFFER)" />
        <vertical-line />
      </div>

      <flow-step-cancel-survey
        v-if="surveyStep && surveyStep.enabled"
        :step="surveyStep"
        ref="survey"
        @update="(update, field) => stepUpdate(surveyStep, update, field)"
        :key="surveyStep.guid"
      />
      <flow-step-cancel-survey-empty v-else @cta-click="enableStep(STEP_PLACE.SURVEY)" />
      <vertical-line />
      <flow-step-freeform
        v-if="freeformStep && freeformStep.enabled"
        :step="freeformStep"
        ref="free"
        @update="(update, field) => stepUpdate(freeformStep, update, field)"
        @rework="reworkStep(freeformStep)"
        :key="freeformStep.guid"
      />
      <flow-step-freeform-empty v-else @cta-click="enableStep(STEP_PLACE.FREEFORM)" />
      <vertical-line />
      <flow-step-offer
        v-if="finalOfferStep && finalOfferStep.enabled"
        :step="finalOfferStep"
        ref="offer"
        @update="(update, field) => stepUpdate(finalOfferStep, update, field)"
        :key="finalOfferStep.guid"
        @rework="reworkStep(finalOfferStep)"
        :isFinalOffer="true"
      />
      <flow-step-final-offer-empty v-else :step="finalOfferStep" @cta-click="enableStep(STEP_PLACE.FINAL_OFFER)" />
      <vertical-line />
      <flow-step-cancel-confirm
        v-if="confirmStep && confirmStep.enabled"
        :step="confirmStep"
        ref="confirm"
        @update="(update, field) => stepUpdate(confirmStep, update, field)"
        @rework="reworkStep(confirmStep)"
        :key="confirmStep.guid"
      />
      <flow-step-cancel-confirm-empty v-else @cta-click="enableStep(STEP_PLACE.CONFIRM)" />
      <vertical-line />
      <!-- Save & Advance -->
      <div class="text-center" v-if="!generating">
        <button
          type="button"
          @click="nextStep"
          class="inline-flex items-center px-6 py-2 border-2 border-brand-sand shadow-sm text-base font-semibold rounded-xl text-brand-brown bg-brand-light-sand hover:bg-brand-sand focus:outline-none focus:opacity-80 transition-colors"
        >
          Continue to Offers →
        </button>
      </div>
    </div>
    <el-dialog class="p-4" custom-class="container max-w-xl rounded-lg p-0" :visible.sync="showConfirmTemplate" :close-on-click-modal="true">
      <div class="px-6 py-8">
        <p class="font-bold text-brand-black text-xl">Discard changes?</p>
        <p class="text-brand-brown font-medium text-base mt-2">You're about to overwrite your cancel flow with a new template.</p>
      </div>
      <div class="border-t-2 border-brand-sand bg-brand-light-sand py-6 rounded-b-lg">
        <div class="flex justify-end px-8 space-x-4">
          <el-button type="text" class="font-semibold text-md" @click="rejectTemplateChange">Go Back</el-button>
          <el-button type="primary" class="font-semibold text-md" @click="prefillTemplate(selectedTemplate)">Apply Template</el-button>
        </div>
      </div>
    </el-dialog>
    <AIJobMonitor />
  </div>
</template>

<script>
import {
  STEP_TYPE,
  createBaseStep,
  defaultStepOrder,
  OFFER_TYPE,
  AI_JOB_ESTIMATED_DURATION,
  STEP_PLACE,
  PLACE_TO_STEP_TYPE,
  insertNewStep,
} from '@/store/shared';
import { duplicateSteps, generateTemplateSteps } from '@/helpers/templates';
import { SET_BLUEPRINT_STEP, SET_SELECTED_TEMPLATE, STORE_BLUEPRINT_DATA } from '@/store/mutation-types';
import { BLUEPRINT_SCHEDULE_SAVE, AI_REWORK_STEP } from '@/store/action-types';
import VerticalLine from '@/components/UI/VerticalLine.vue';
import NewTemplatePicker from '@/components/Builder/NewTemplatePicker.vue';
import FlowStepOffer from '@/components/FlowSteps/FlowStepOffer.vue';
import FlowStepOfferEmpty from '@/components/FlowSteps/FlowStepOfferEmpty.vue';
import FlowStepFinalOfferEmpty from '@/components/FlowSteps/FlowStepFinalOfferEmpty.vue';
import FlowStepCancelSurvey from '@/components/FlowSteps/FlowStepCancelSurvey.vue';
import FlowStepCancelSurveyEmpty from '@/components/FlowSteps/FlowStepCancelSurveyEmpty.vue';
import FlowStepFreeform from '@/components/FlowSteps/FlowStepFreeform.vue';
import FlowStepFreeformEmpty from '@/components/FlowSteps/FlowStepFreeformEmpty.vue';
import FlowStepCancelConfirm from '@/components/FlowSteps/FlowStepCancelConfirm.vue';
import FlowStepCancelConfirmEmpty from '@/components/FlowSteps/FlowStepCancelConfirmEmpty.vue';
import { jobAffectsBlueprint, isChild } from '@/helpers/ai-jobs';
import { mapGetters } from 'vuex';
import NewAIBuilder from './NewAIBuilder.vue';
import AIJobMonitor from './AIJobMonitor.vue';

export default {
  name: 'FlowBuilder',
  components: {
    VerticalLine,
    NewTemplatePicker,
    NewAIBuilder,
    FlowStepFinalOfferEmpty,
    FlowStepOfferEmpty,
    FlowStepOffer,
    FlowStepCancelSurvey,
    FlowStepCancelSurveyEmpty,
    FlowStepFreeform,
    FlowStepFreeformEmpty,
    FlowStepCancelConfirm,
    FlowStepCancelConfirmEmpty,
    AIJobMonitor,
  },
  data() {
    let newBlueprint = true;
    if (this.$store.getters.currentBlueprint) {
      newBlueprint = this.$store.getters.currentBlueprint.steps.length === 0;
    }
    return {
      STEP_TYPE,
      STEP_PLACE,
      showAIBuildModal: false,
      loading: false,
      // confirm before using template if blueprint has been edited before
      confirmTemplatePick: !newBlueprint,
      confirmAiOverride: !newBlueprint,
      showConfirmTemplate: false,
      selectedTemplate: this.$store.state.selectedTemplate || null,
      generatedData: null,
    };
  },
  computed: {
    ...mapGetters([
      'orgCanPause',
      'orgHasPublishedBlueprint',
      'isLiteSubscriber',
      'currentSegment',
      'primaryBlueprint',
      'missingPauseCoupon',
      'aiJobsInProgress',
      'validateBlueprintSteps',
    ]),
    coupons() {
      return this.$store.getters.filteredCoupons;
    },
    // does any active job involve this blueprint?
    disableAIBuilder() {
      if (this.aiJobsInProgress && this.aiJobsInProgress.length > 0) {
        const jobs = this.aiJobsInProgress.filter((j) => isChild(j, this.blueprintGuid));
        if (jobs.length > 0) {
          return true;
        }
      }
      return false;
    },
    // is any job affecting this whole blueprint?
    aiJob() {
      if (this.aiJobsInProgress && this.aiJobsInProgress.length > 0) {
        const job = this.aiJobsInProgress.find((j) => jobAffectsBlueprint(j, this.blueprintGuid));
        if (job) {
          return job;
        }
      }
      return null;
    },
    generating() {
      return this.aiJob !== null;
    },
    aiJobCreatedAt() {
      if (this.aiJob) {
        return this.aiJob.createdAt;
      }
      return null;
    },
    aiEstimatedTime() {
      if (this.aiJobCreatedAt) {
        const createdAt = new Date(this.aiJobCreatedAt);
        const estimatedCompletionTime = new Date(createdAt.getTime() + AI_JOB_ESTIMATED_DURATION * 1000); // 90 seconds
        return estimatedCompletionTime;
      }
      return null;
    },
    blueprint() {
      return this.$store.getters.currentBlueprint;
    },
    blueprintGuid() {
      if (!this.$store.getters.currentBlueprint) {
        return null;
      }
      return this.$store.getters.currentBlueprint.guid;
    },
    isNew() {
      return !this.blueprint || this.blueprint.version === 1;
    },
    placeMap() {
      return this.$store.getters.blueprintPlaceMap;
    },
    initialOfferStep() {
      return this.placeMap[STEP_PLACE.INITIAL_OFFER];
    },
    surveyStep() {
      return this.placeMap[STEP_PLACE.SURVEY];
    },
    freeformStep() {
      return this.placeMap[STEP_PLACE.FREEFORM];
    },
    finalOfferStep() {
      return this.placeMap[STEP_PLACE.FINAL_OFFER];
    },
    confirmStep() {
      return this.placeMap[STEP_PLACE.CONFIRM];
    },
  },

  methods: {
    scheduleSave() {
      this.confirmTemplatePick = true;
      this.confirmAiOverride = true;
      this.$store.dispatch(BLUEPRINT_SCHEDULE_SAVE, { id: this.blueprint._id });
    },
    stepUpdate(step, update, field) {
      // nested updated on the step
      if (field) {
        const existingField = step[field];
        const fieldUpdate = {
          ...existingField,
          ...update,
        };
        this.$store.commit(SET_BLUEPRINT_STEP, {
          ...step,
          [field]: fieldUpdate,
        });
      } else {
        this.$store.commit(SET_BLUEPRINT_STEP, {
          ...step,
          ...update,
        });
      }

      this.scheduleSave();
    },
    nextStep() {
      this.$emit('next');
    },
    async reworkStep(step) {
      let additionalData = {};
      if (step && step.stepType === STEP_TYPE.OFFER) {
        if (step.offer && step.offer.offerType === OFFER_TYPE.DISCOUNT) {
          const {
            discountConfig: { couponId },
          } = step.offer;
          const coupon = this.coupons.find((c) => c.id === couponId);
          if (coupon) {
            additionalData = { couponData: coupon };
          }
        }
      }
      await this.$store.dispatch(AI_REWORK_STEP, {
        blueprintGuid: this.blueprintGuid,
        stepGuid: step.guid,
        step,
        additionalData,
      });
    },
    async enableStep(stepPlace) {
      const type = PLACE_TO_STEP_TYPE[stepPlace];
      const existingStep = this.placeMap[stepPlace];
      // step already exists, just enable it
      if (existingStep) {
        if (type === STEP_TYPE.OFFER && existingStep.offer?.offerType === OFFER_TYPE.PAUSE && !this.orgCanPause) {
          // org cannot pause, change offer type to discount
          const newStepOptions = { offerType: OFFER_TYPE.DISCOUNT };
          const newStep = createBaseStep(type, newStepOptions);
          this.$store.commit(SET_BLUEPRINT_STEP, {
            ...existingStep,
            ...newStep,
            guid: existingStep.guid,
          });
        } else {
          this.$store.commit(SET_BLUEPRINT_STEP, {
            ...existingStep,
            enabled: true,
          });
        }
        this.scheduleSave();
      } else {
        // step doesn't exist, we need to create it
        this.loading = true;
        const newStepOptions = {};
        if (type === STEP_TYPE.OFFER) {
          newStepOptions.offerType = this.orgCanPause ? OFFER_TYPE.PAUSE : OFFER_TYPE.DISCOUNT;
        }
        const newStep = createBaseStep(type, newStepOptions);
        const newSteps = insertNewStep(this.blueprint.steps, newStep, stepPlace);
        this.$store.commit(STORE_BLUEPRINT_DATA, { id: this.blueprint._id, data: { steps: newSteps } });
        this.scheduleSave();
        this.loading = false;
      }
    },
    handleTemplateChange(templateName) {
      this.selectedTemplate = templateName;
      if (templateName) {
        if (this.confirmTemplatePick) {
          this.showConfirmTemplate = true;
        } else {
          this.prefillTemplate(templateName);
        }
      }
    },
    rejectTemplateChange() {
      this.selectedTemplate = '';
      this.showConfirmTemplate = false;
    },
    validateSteps(steps) {
      return this.validateBlueprintSteps(steps);
    },
    async prefillTemplateFromPrimary() {
      // from primary blueprint - can add more later
      if (this.primaryBlueprint && this.primaryBlueprint.steps) {
        this.loading = true;
        this.showConfirmTemplate = false;
        const templateSteps = duplicateSteps(this.primaryBlueprint.steps);
        const validatedSteps = this.validateSteps(templateSteps);
        this.$store.commit(STORE_BLUEPRINT_DATA, { id: this.blueprint._id, data: { steps: validatedSteps } });
        this.scheduleSave();
        this.confirmTemplatePick = false;
        this.loading = false;
      }
    },
    async prefillTemplateFromSegment(segmentId) {
      const segments = this.$store.state.segments;
      const segment = segments[segmentId];
      let blueprintId;
      if (segment) {
        if (segment.activeBlueprint) {
          blueprintId = segment.activeBlueprint;
        }
        if (segment.blueprints?.length) {
          blueprintId = segment.blueprints[0];
        }
      }
      if (blueprintId && this.$store.state.blueprints[blueprintId]) {
        const blueprintToCopy = this.$store.state.blueprints[blueprintId];
        this.loading = true;
        this.showConfirmTemplate = false;
        const templateSteps = duplicateSteps(blueprintToCopy.steps);
        const validatedSteps = this.validateSteps(templateSteps);
        this.$store.commit(STORE_BLUEPRINT_DATA, { id: this.blueprint._id, data: { steps: validatedSteps } });
        this.scheduleSave();
        this.confirmTemplatePick = false;
        this.loading = false;
      }
    },
    async prefillTemplate(templateName = 'BASIC') {
      if (templateName === 'primary') {
        this.prefillTemplateFromPrimary();
      } else if (templateName.includes('segment-')) {
        const segmentId = templateName.replace('segment-', '');
        this.prefillTemplateFromSegment(segmentId);
      } else {
        this.loading = true;
        this.showConfirmTemplate = false;
        const templateSteps = generateTemplateSteps(templateName);
        const validatedSteps = this.validateSteps(templateSteps);
        this.$store.commit(STORE_BLUEPRINT_DATA, { id: this.blueprint._id, data: { steps: validatedSteps } });
        this.$store.commit(SET_SELECTED_TEMPLATE, templateName);
        this.scheduleSave();
        this.confirmTemplatePick = false;
        this.loading = false;
      }
    },
  },
};
</script>

<style>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.5s ease;
}
.fade-enter,
.fade-leave-to {
  opacity: 0;
}

.slide-up-down-enter-active,
.slide-up-down-leave-active {
  transition: all 0.3s ease;
}

.slide-up-down-enter,
.slide-up-down-leave-to {
  opacity: 0;
  transform: translateY(-20px);
}
</style>
